import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Button } from '@/components/shadcn/button'
import { Calendar } from '@/components/shadcn/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { BaseFieldProps } from './types'
import { IconCalendarClockOutline } from '@icons/IconCalendarClockOutline'
import clsx from 'clsx'
import { Label } from '@/components/shadcn/label'
import { Input } from '@/components/shadcn/input'
import { IconTimeOutline } from '@icons/IconTimeOutline'
import { useId } from 'react'
import { DocumentationButton } from './DocumentationButton'
import { getDatePickerCalendarProps } from '@/lib/datePickerCalendarProps'

export const DateTimeField2: React.FC<BaseFieldProps> = ({ field, formFieldName, control }) => {
  const id = useId()
  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        const selectedDate = formField.value ? new Date(formField.value as string) : undefined
        const validSelectedDate =
          selectedDate && !Number.isNaN(selectedDate.getTime()) ? selectedDate : undefined

        return (
          <FormItem className={clsx('flex flex-1 flex-col', field.widget === 'hidden' && 'hidden')}>
            <div className="flex justify-start gap-2">
              <FormLabel>{field.title}</FormLabel>
              <DocumentationButton documentation={field.documentation} />
            </div>
            <FormDescription className="text-xs">{field.description}</FormDescription>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="input"
                  className={cn('w-full pl-3 text-left font-normal', !formField.value && 'text-muted-foreground')}
                  disabled={field.readOnly}
                >
                  {formField.value ? (
                    format(new Date(formField.value as string), 'PPpp')
                  ) : (
                    <span>Select Date and Time</span>
                  )}
                  <IconCalendarClockOutline className="ml-auto size-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <div className="sm:flex sm:flex-col">
                  <Calendar
                    mode="single"
                    className="p-2"
                    selected={validSelectedDate}
                    onSelect={(date) => {
                      if (date) {
                        const formattedDate = format(
                          date,
                          field.format === 'date' ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'HH:mm:ss"
                        )
                        formField.onChange(formattedDate)
                      }
                    }}
                    {...getDatePickerCalendarProps(validSelectedDate)}
                  />
                  <div className="border-t p-3">
                    <div className="flex items-center gap-3">
                      <Label htmlFor={id} className="text-xs">
                        Enter time
                      </Label>
                      <div className="relative grow">
                        <Input
                          id={id}
                          type="time"
                          step="1"
                          value={formField.value ? format(new Date(formField.value as string), 'HH:mm:ss') : ''}
                          onChange={(e) => {
                            const newDate = new Date(formField.value as string)
                            newDate.setHours(parseInt(e.target.value.split(':')[0]))
                            newDate.setMinutes(parseInt(e.target.value.split(':')[1]))
                            newDate.setSeconds(parseInt(e.target.value.split(':')[2]))
                            const formatted = format(newDate, "yyyy-MM-dd'T'HH:mm:ss")
                            formField.onChange(formatted)
                          }}
                          className="peer appearance-none ps-9 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        />
                        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
                          <IconTimeOutline size={16} aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
